import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from '../ApplicationState';
import SpinnerOverlay from '../SpinnerOverlay';
import StudentHeroSegment from '../components/StudentHeroSegment';
import { listStudentAssignments, retrieveAssignment, getMiddleSchoolModule } from '../BackendInterface';
import { getHighSchoolUnits } from '../components/LibraryCard/cardData';
import { selectClassroom } from '../Classrooms';
import { Assignment, Module } from '@peekapak/types/LessonPlanTypes';
import toDoHeaderHS from '../images/ToDo_Page_HS.png';
import StrengthsCircle from '../components/StrengthsCircle';
import { StrengthsEvaluation } from '../components/StrengthsCircle/StrengthsCircle';
import Button from '../components/Button';
import StrengthsCircleMobile from '../components/StrengthsCircleMobile';
import styles from './StudentStrengths.module.scss';
import { getStrengthsModuleIds } from './strengthsHelpers';

const StudentStrengths = () => {
  const [moduleData, setModuleData] = useState<Module[]>([]);
  const [isModuleDataLoaded, setIsModuleDataLoaded] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState<string>('');
  const [clickedAssignment, setClickedAssignment] = useState<Assignment | null>(null);
  const [strength, setStrength] = useState<string>('');
  const [assignments, setAssignments] = useState<Assignment[]>();

  const classroom = useSelector((state: RootState) => {
    return selectClassroom(state);
  });

  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });

  const history = useHistory();

  const language = classroom?.language || 'en';

  const isSmall = useMediaQuery({ query: '(max-width: 820px)' });

  const handleAssessmentClick = (unit: string, assignment: Assignment, strength: string) => {
    setStrength(strength[0]);
    setSelectedUnit(unit);
    setClickedAssignment(assignment);
  };

  const handleCompleteToDoClick = (assignment: Assignment) => {
    if (!userProfile) return;
    history.push(
      `/studentportal/activity/${assignment.module}/${assignment.language ?? language}/${userProfile.grade}/toDo/${assignment.createdAt}`,
    );
  };

  const getStrengthsAssessments = async () => {
    try {
      if (userProfile) {
        const studentToDos = await listStudentAssignments(userProfile.userId, language, userProfile.grade);
        const strenghtsEvals = studentToDos.filter((assignments) => assignments.module.includes('self-assessment'));
        const completedEvalsWork = await Promise.all(
          strenghtsEvals.map((strengthsEval) => {
            const work = retrieveAssignment(userProfile.userId, strengthsEval.createdAt);
            return work;
          }),
        );
        const arrayRemoved = completedEvalsWork.map((item) => item[0]);
        setAssignments(arrayRemoved);
      }
    } catch {
      console.error('Error fetching student ToDos');
    } finally {
      setIsLoaded(true);
    }
  };

  const loadModuleData = useCallback(async () => {
    try {
      const moduleIds = getStrengthsModuleIds();
      const moduleData = await Promise.all(
        moduleIds.map(async (moduleId) => {
          const thisModule = await getMiddleSchoolModule(moduleId, language, userProfile?.grade.toString() ?? '9');
          return thisModule.data as unknown as Module;
        }),
      );
      console.log(moduleData);
      setModuleData(moduleData);
      setIsModuleDataLoaded(true);
    } catch {
      console.error('Error fetching strenghts content');
    }
  }, [classroom]);

  useEffect(() => {
    if (!isLoaded) getStrengthsAssessments();
  }, [isLoaded]);

  useEffect(() => {
    if (!isModuleDataLoaded) loadModuleData();
  }, [isModuleDataLoaded]);

  const clickedModuleData = moduleData
    .find((strengthsModule) => strengthsModule.id === clickedAssignment?.module)
    ?.studentContent[0].content.find((item) => item.type === 'strengthsEvaluation') as unknown as StrengthsEvaluation;
  const strengthDescription = clickedModuleData?.content.find((item) => item.strength === strength)?.description;

  return (
    <>
      <SpinnerOverlay isShow={!isLoaded} />
      <StudentHeroSegment home backgroundImage={toDoHeaderHS}>
        <div className={styles.heroText}>
          <h1>My Strengths</h1>
          <h3>
            {`Click on the unit name in the wheel to review`}
            <br />
            {`your self-assessment results from each unit`}
          </h3>
        </div>
      </StudentHeroSegment>
      {isLoaded && (
        <div className={styles.container}>
          <div className={styles.titleRow}>
            {isSmall ? (
              <StrengthsCircleMobile
                handleUnitClick={handleAssessmentClick}
                assignments={assignments ?? []}
                selectedUnit={selectedUnit}
                data={moduleData}
              />
            ) : (
              <StrengthsCircle
                handleUnitClick={handleAssessmentClick}
                assignments={assignments ?? []}
                selectedUnit={selectedUnit}
                data={moduleData}
              />
            )}
          </div>
          <div className={styles.messageContainer}>
            {strength === '' && <p>Click on a unit name in the wheel to see your SELf assessment results!</p>}
            {strength === 'incomplete' &&
              ['inProgress', 'retracted', 'assigned'].includes(clickedAssignment?.status) && (
                <div className={styles.incompleteContainer}>
                  <p>
                    {`You've not yet completed the self-assessment for this unit. Click the button below to complete the assignment. Your result will be displayed here when it's done.`}
                  </p>
                  <Button darkBlue thinText small onClick={() => handleCompleteToDoClick(clickedAssignment!)}>
                    Complete Assignment
                  </Button>
                </div>
              )}
            {strength === 'incomplete' &&
              !['inProgress', 'retracted', 'assigned'].includes(clickedAssignment?.status) && (
                <p>
                  {`The self-assessment for this unit has not yet been assigned. You'll be able to see your result here once you've completed it.`}
                </p>
              )}
            {strength !== 'incomplete' && clickedAssignment?.status === 'submitted' && (
              <div className={styles.assessmentContainer}>
                <h3>
                  <u>Your Strength is: {strength}</u>
                </h3>
                <div
                  dangerouslySetInnerHTML={{
                    __html: strengthDescription ?? '',
                  }}
                />
                <div className={styles.reviewLink}>
                  <p role='button' onClick={() => handleCompleteToDoClick(clickedAssignment!)}>
                    <u>Review my SELf Assessment Answers</u>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StudentStrengths;
